import type { ServerMetricsDto } from "@puszczyk/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, unwrap } from "../../lib/api";

const POLL_MS = 5000;
const HISTORY = 30; // ~2.5 min of samples for the sparklines

/** Latest server metrics plus a short rolling history of CPU/RAM/disk fractions for the sparklines. */
export type ServerHealth = {
  latest: ServerMetricsDto | null;
  cpuHistory: number[];
  memHistory: number[];
  diskHistory: number[];
  error: boolean;
};

export function useServerMetrics(enabled: boolean): ServerHealth {
  const query = useQuery({
    queryKey: ["platform", "metrics"],
    queryFn: () => unwrap(api.platform.metrics.$get()),
    refetchInterval: POLL_MS,
    enabled,
    retry: false,
  });

  const [cpuHistory, setCpu] = useState<number[]>([]);
  const [memHistory, setMem] = useState<number[]>([]);
  const [diskHistory, setDisk] = useState<number[]>([]);
  const lastAt = useRef<string | null>(null);

  const data = query.data ?? null;
  useEffect(() => {
    if (!data || data.at === lastAt.current) return;
    lastAt.current = data.at;
    const push = (arr: number[], v: number) => [...arr, v].slice(-HISTORY);
    // CPU is null on the very first sample (no delta yet) — hold the line flat until the next one.
    setCpu((h) => push(h, data.cpu ?? h[h.length - 1] ?? 0));
    setMem((h) => push(h, data.memory.used / data.memory.total));
    setDisk((h) => push(h, data.disk.used / data.disk.total));
  }, [data]);

  return { latest: data, cpuHistory, memHistory, diskHistory, error: query.isError };
}
